import { useState } from "react";

import { Icon, Span, Tag } from "@virtual-brain-lib/frontend-design-system/ui";
import type {
	MessageToolDeepDiffComparison,
	MessageToolDeepDiffData,
} from "@virtual-brain-lib/types";

import { ToolCard, ToolCardSteps } from "../core";
import { DeepDiffModal } from "./DeepDiffModal";
import { SemanticDiffModal } from "./SemanticDiffModal";

function getDiffDensity(comparison: MessageToolDeepDiffComparison): number {
	if (comparison.mode === "quick-scan") {
		return comparison.diffPercent;
	}

	return comparison.changeRate;
}

function getDensityColor(percent: number): "success" | "warning" | "danger" {
	if (percent <= 20) return "success";
	if (percent <= 50) return "warning";

	return "danger";
}

function ComparisonRow({
	comparison,
	onClick,
}: {
	readonly comparison: MessageToolDeepDiffComparison;
	readonly onClick: () => void;
}) {
	const density = getDiffDensity(comparison);

	return (
		<button
			type="button"
			className="flex w-full items-center gap-1.5 rounded px-1.5 py-1 text-left transition-colors hover:bg-secondary-hover"
			onClick={onClick}
		>
			<Span variant="body-xs" className="min-w-0 flex-1 truncate" color="default">
				{comparison.sourceDocument.name}
			</Span>
			<Icon name="diff" size="md" color="primary" />
			<Span variant="body-xs" className="min-w-0 flex-1 truncate" color="default">
				{comparison.targetDocument.name}
			</Span>
			<Tag color={getDensityColor(density)} size="xs">
				{density}%
			</Tag>
		</button>
	);
}

export function ToolCardDeepDiff({
	state,
	className,
}: {
	readonly state: MessageToolDeepDiffData;
	readonly className?: string;
}) {
	const { status, statusText, steps, sourceDocument, targetDocument, comparisons } = state;
	const [activeComparison, setActiveComparison] = useState<MessageToolDeepDiffComparison | null>(
		null,
	);

	if (status !== "processing" && steps.length === 0) {
		return null;
	}

	const getDefaultStatusText = () => {
		if (status === "processing") {
			return "Comparing documents";
		}

		if (sourceDocument && targetDocument) {
			return `Compared "${sourceDocument.name}" with "${targetDocument.name}"`;
		}

		return "Comparison complete";
	};

	const getCompactSummary = () => {
		if (status === "processing") {
			return "Comparing";
		}

		return "Comparison done";
	};

	const displayedStatusText = statusText ?? getDefaultStatusText();

	return (
		<>
			<ToolCard
				className={className}
				icon="files"
				status={status}
				statusText={displayedStatusText}
				compactSummary={getCompactSummary()}
				title="Deep Diff"
			>
				{steps.length > 0 && <ToolCardSteps steps={steps} />}
				{comparisons.length > 0 && (
					<div className="flex flex-col">
						{comparisons.map((comparison) => (
							<ComparisonRow
								key={comparison.id}
								comparison={comparison}
								onClick={() => {
									setActiveComparison(comparison);
								}}
							/>
						))}
					</div>
				)}
			</ToolCard>

			{activeComparison?.mode === "quick-scan" && (
				<DeepDiffModal
					isOpen
					close={() => {
						setActiveComparison(null);
					}}
					sourceText={activeComparison.sourceText}
					targetText={activeComparison.targetText}
					sourceDocumentName={activeComparison.sourceDocument.name}
					targetDocumentName={activeComparison.targetDocument.name}
				/>
			)}

			{activeComparison?.mode === "semantic-compare" && (
				<SemanticDiffModal
					isOpen
					close={() => {
						setActiveComparison(null);
					}}
					chunks={activeComparison.chunks}
					sourceDocumentName={activeComparison.sourceDocument.name}
					targetDocumentName={activeComparison.targetDocument.name}
				/>
			)}
		</>
	);
}
